import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import LoyaltyAdjustModal from "./LoyaltyAdjustModal";
import Toast from "./Toast";
import { apiErrorMessage } from "../services/api";
import { getCurrentUser } from "../services/authService";
import { fetchBranches } from "../services/branchService";
import { formatDateTime } from "../services/datetime";
import {
  cancelRedemption,
  claimReward,
  createCard,
  fetchCard,
  fetchTransactions,
  redeemCode,
} from "../services/loyaltyService";
import { can } from "../services/permissions";
import { formatCode } from "../services/shortCode";
import { labelFor } from "../services/enumLabels";

/** How many stamp circles to draw before switching to a number. */
const MAX_CIRCLES = 20;

/** Where a ledger row's source leads, when it leads anywhere. */
const sourceLink = (row) => {
  if (row.source_type === "appointment" && row.source_id) return `/appointments/${row.source_id}`;
  if (row.appointment_id) return `/appointments/${row.appointment_id}`;
  return null;
};

/**
 * One client's loyalty: balance, stamps, tier, the rewards on offer (and why
 * one cannot be claimed yet), the rewards already claimed, and the ledger.
 * Used by the member page and the client profile's Loyalty tab. Claiming and
 * using need `loyalty.redeem`; moving the balance by hand `loyalty.adjust`.
 */
export default function LoyaltyPanel({ clientId }) {
  const { t, i18n } = useTranslation();
  const user = getCurrentUser();
  const mayRedeem = can(user, "loyalty.redeem");
  const mayAdjust = can(user, "loyalty.adjust");

  const [card, setCard] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [toast, setToast] = useState(null);
  // Using a reward needs the branch it is used at (the server requires it).
  // The list is this user's own branches.
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");

  useEffect(() => {
    if (!mayRedeem) return;
    fetchBranches(1, 100)
      .then(({ branches: list }) => {
        setBranches(list ?? []);
        setBranchId((current) => current || list?.[0]?.id || "");
      })
      .catch(() => {});
  }, [mayRedeem]);

  const loadCard = useCallback(() => {
    fetchCard(clientId)
      .then(setCard)
      .catch((err) => setError(apiErrorMessage(err, t("loyalty.loadFailed"))));
  }, [clientId, t]);

  const loadLedger = useCallback(() => {
    fetchTransactions(clientId, { page })
      .then(setLedger)
      .catch((err) => setError(apiErrorMessage(err, t("loyalty.loadFailed"))));
  }, [clientId, page, t]);

  useEffect(loadCard, [loadCard]);
  useEffect(loadLedger, [loadLedger]);

  const refresh = () => {
    loadCard();
    loadLedger();
  };

  const act = async (key, work, success) => {
    setBusy(key);
    try {
      const result = await work();
      setToast({ type: "success", message: success(result) });
      refresh();
    } catch (err) {
      setToast({ type: "error", message: apiErrorMessage(err, t("loyalty.actionFailed")) });
    } finally {
      setBusy("");
    }
  };

  if (error && !card) {
    return (
      <div
        className="rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text"
        role="alert"
      >
        {error}
      </div>
    );
  }

  if (!card) return <p className="py-12 text-center text-sm text-ink-2">{t("common.loading")}</p>;

  const required = card.stamps?.required ?? 0;
  const stamps = card.stamps?.balance ?? 0;
  const filled = required > 0 ? Math.min(stamps, required) : 0;

  return (
    <div className="space-y-4">
      {/* Where rewards are claimed and used from this card. */}
      {mayRedeem && branches.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <label className="field-label mb-0" htmlFor="lp-branch">
            {t("loyalty.redeem.branch")}
          </label>
          <select
            id="lp-branch"
            className="field-input w-auto"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
          >
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Reading a card never opens one: offer the explicit action. */}
      {card.has_account === false && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-line bg-raised px-3 py-2 text-sm">
          <span className="text-ink-2">{t("loyalty.card.noAccount")}</span>
          {mayRedeem && (
            <button
              type="button"
              className="btn-secondary"
              disabled={busy === "create"}
              onClick={() =>
                act(
                  "create",
                  () => createCard(clientId),
                  () => t("loyalty.card.created")
                )
              }
            >
              {t("loyalty.card.create")}
            </button>
          )}
        </div>
      )}

      {/* Balances */}
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="card p-4">
          <p className="text-xs uppercase tracking-wide text-ink-2">{t("loyalty.card.points")}</p>
          <p className="mt-1 text-2xl font-bold tabular-nums" dir="ltr">
            {card.points.balance}
          </p>
          <p className="text-xs text-ink-2">{t("loyalty.card.lifetime", { count: card.points.lifetime })}</p>
        </div>

        <div className="card p-4">
          <p className="text-xs uppercase tracking-wide text-ink-2">{t("loyalty.card.stamps")}</p>
          {required > 0 ? (
            <>
              {required <= MAX_CIRCLES ? (
                <div
                  className="mt-2 flex flex-wrap gap-1"
                  aria-label={t("loyalty.card.stampsOf", { count: stamps, required })}
                >
                  {Array.from({ length: required }, (_, i) => (
                    <span
                      key={i}
                      className={`h-4 w-4 rounded-full border ${i < filled ? "border-accent bg-accent" : "border-line-strong"}`}
                    />
                  ))}
                </div>
              ) : (
                <div
                  className="mt-3 h-2 rounded-full bg-raised"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={required}
                  aria-valuenow={filled}
                >
                  <div
                    className="h-2 rounded-full bg-accent"
                    style={{ width: `${(filled / required) * 100}%` }}
                  />
                </div>
              )}
              <p className="mt-2 text-sm" dir="auto">
                {t("loyalty.card.stampsOf", { count: stamps, required })}
              </p>
            </>
          ) : (
            <p className="mt-1 text-2xl font-bold tabular-nums">{stamps}</p>
          )}
        </div>

        <div className="card p-4">
          <p className="text-xs uppercase tracking-wide text-ink-2">{t("loyalty.card.tier")}</p>
          <p className="mt-1 text-lg font-bold">{card.tier?.name ?? t("common.none")}</p>
          {card.next_tier && (
            <p className="text-xs text-ink-2">
              {t("loyalty.card.nextTier", { name: card.next_tier.name, count: card.next_tier.points_away })}
            </p>
          )}
        </div>
      </div>

      {mayAdjust && (
        <div className="flex justify-end">
          <button type="button" className="btn-secondary" onClick={() => setAdjustOpen(true)}>
            {t("loyalty.adjust.open")}
          </button>
        </div>
      )}

      {/* Claimed, not yet used */}
      {card.outstanding?.length > 0 && (
        <section className="card p-4">
          <h3 className="mb-3 font-semibold">{t("loyalty.card.outstanding")}</h3>
          <ul className="divide-y divide-line">
            {card.outstanding.map((row) => (
              <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <div>
                  <p className="font-medium">{row.reward?.name}</p>
                  <p className="text-xs text-ink-2">
                    <span className="font-mono" dir="ltr">
                      {row.code_display ?? formatCode(row.code)}
                    </span>
                    {row.expires_at && (
                      <>
                        {" "}
                        ·{" "}
                        {t("loyalty.card.validUntil", {
                          date: formatDateTime(row.expires_at, i18n.language),
                        })}
                      </>
                    )}
                  </p>
                </div>
                {mayRedeem && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="btn-primary"
                      disabled={busy !== ""}
                      onClick={() =>
                        act(
                          `use-${row.id}`,
                          () => redeemCode({ code: row.code, branchId }),
                          () => t("loyalty.redeem.used", { name: row.reward?.name })
                        )
                      }
                    >
                      {t("loyalty.card.useNow")}
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      disabled={busy !== ""}
                      onClick={() =>
                        act(
                          `back-${row.id}`,
                          () => cancelRedemption(row.id),
                          () => t("loyalty.card.givenBack")
                        )
                      }
                    >
                      {t("loyalty.card.giveBack")}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Rewards on offer */}
      <section className="card p-4">
        <h3 className="mb-3 font-semibold">{t("loyalty.card.rewards")}</h3>
        {card.rewards?.length ? (
          <ul className="divide-y divide-line">
            {card.rewards.map((reward) => (
              <li key={reward.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <div>
                  <p className="font-medium">{reward.name}</p>
                  <p className="text-xs text-ink-2">
                    {[
                      reward.points_required > 0 &&
                        t("loyalty.pointsCount", { count: reward.points_required }),
                      reward.stamps_required > 0 &&
                        t("loyalty.stampsCount", { count: reward.stamps_required }),
                    ]
                      .filter(Boolean)
                      .join(" + ")}
                    {!reward.can_claim && reward.blocked_by?.[0] && (
                      <span className="ms-2 text-warning-text">
                        {t(`loyalty.blocked.${reward.blocked_by[0]}`, {
                          points: reward.points_short,
                          stamps: reward.stamps_short,
                        })}
                      </span>
                    )}
                  </p>
                </div>
                {mayRedeem && (
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={!reward.can_claim || busy !== ""}
                    onClick={() =>
                      act(
                        `claim-${reward.id}`,
                        () => claimReward(clientId, { rewardId: reward.id, branchId }),
                        (result) => t("loyalty.card.claimed", { code: result.redemption?.code })
                      )
                    }
                  >
                    {t("loyalty.card.claim")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-ink-2">{t("loyalty.card.noRewards")}</p>
        )}
      </section>

      {/* The ledger */}
      <section className="card overflow-hidden">
        <h3 className="px-4 pt-4 font-semibold">{t("loyalty.ledger.title")}</h3>
        {ledger === null ? (
          <p className="py-8 text-center text-sm text-ink-2">{t("common.loading")}</p>
        ) : ledger.transactions.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-ink-2">{t("loyalty.ledger.empty")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="mt-2 w-full text-start text-sm">
              <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-ink-2">
                <tr>
                  <th className="px-4 py-2 text-start">{t("loyalty.ledger.date")}</th>
                  <th className="px-4 py-2 text-start">{t("loyalty.ledger.type")}</th>
                  <th className="px-4 py-2 text-end">{t("loyalty.ledger.points")}</th>
                  <th className="px-4 py-2 text-end">{t("loyalty.ledger.stamps")}</th>
                  <th className="px-4 py-2 text-start">{t("loyalty.ledger.reason")}</th>
                  <th className="px-4 py-2 text-start">{t("loyalty.ledger.source")}</th>
                  <th className="px-4 py-2 text-start">{t("loyalty.ledger.by")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {ledger.transactions.map((row) => {
                  const link = sourceLink(row);
                  return (
                    <tr key={row.id}>
                      <td className="whitespace-nowrap px-4 py-2 text-ink-2">
                        {formatDateTime(row.created_at, i18n.language)}
                      </td>
                      <td className="px-4 py-2">{labelFor("loyalty_type", row.type)}</td>
                      <td
                        className={`px-4 py-2 text-end font-semibold tabular-nums ${
                          row.points > 0
                            ? "text-accent-text"
                            : row.points < 0
                              ? "text-danger-text"
                              : "text-ink-2"
                        }`}
                        dir="ltr"
                      >
                        {row.points > 0 ? `+${row.points}` : row.points}
                      </td>
                      <td className="px-4 py-2 text-end tabular-nums" dir="ltr">
                        {row.stamps > 0 ? `+${row.stamps}` : row.stamps || ""}
                      </td>
                      <td className="px-4 py-2">
                        {labelFor("loyalty_reason", row.reason)}
                        {row.note && <p className="text-xs text-ink-2">{row.note}</p>}
                      </td>
                      <td className="px-4 py-2">
                        {link ? (
                          <Link to={link} className="text-accent-text hover:underline">
                            {t("loyalty.ledger.openVisit")}
                          </Link>
                        ) : (
                          <span className="text-ink-2">
                            {row.source_type ? labelFor("loyalty_source", row.source_type) : t("common.none")}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-ink-2">
                        {row.created_by_name ?? t("loyalty.ledger.system")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {ledger?.meta?.last_page > 1 && (
          <div className="flex items-center justify-between border-t border-line px-4 py-3 text-sm">
            <button
              type="button"
              className="btn-secondary"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              {t("common.previous")}
            </button>
            <span className="text-ink-2">
              {t("common.pageOf", { current: ledger.meta.current_page, last: ledger.meta.last_page })}
            </span>
            <button
              type="button"
              className="btn-secondary"
              disabled={page >= ledger.meta.last_page}
              onClick={() => setPage(page + 1)}
            >
              {t("common.next")}
            </button>
          </div>
        )}
      </section>

      <LoyaltyAdjustModal
        open={adjustOpen}
        clientId={clientId}
        clientName={card.member?.name}
        balance={card.points.balance}
        onClose={() => setAdjustOpen(false)}
        onDone={() => {
          setToast({ type: "success", message: t("loyalty.adjust.done") });
          refresh();
        }}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
